import {
  APPLICATIONS_VIEW_ONLY_PERMISSION_KEY,
  DOSSIER_TAB_PERMISSION_KEYS,
} from "./applicationHubPermissions";

/**
 * Console Users UI: same section titles and order as the shell sidebar (`ShellNavLinks`).
 */
export type ConsolePermissionModuleDef = {
  id: string;
  /** Matches sidebar section label (e.g. "Leads", "Masters"). */
  title: string;
  description?: string;
  /** Info-only block (no assignable keys), e.g. Projects routes share `applications`. */
  infoOnly?: boolean;
  /** Checkboxes listed before the “full access” row (e.g. Leads / Leads import). */
  prefixGranularKeys?: readonly string[];
  /**
   * When set, this single permission means full access for that submodule (e.g. `applications`
   * vs individual dossier tabs).
   */
  exclusiveFullKey?: string;
  /**
   * Listed after the full-access row but not disabled when full access is on (e.g. view-only dossiers).
   */
  postExclusiveGranularKeys?: readonly string[];
  /** Remaining checkboxes in this module (order matches sidebar items). */
  granularKeys: readonly string[];
};

export const CONSOLE_PERMISSION_MODULES: ConsolePermissionModuleDef[] = [
  {
    id: "menu",
    title: "Menu",
    description: "Home is available to all signed-in console users.",
    granularKeys: ["reports", "geo_tree"],
  },
  {
    id: "leads",
    title: "Leads",
    description:
      "Lead intake first, then applicant registry: use full access for every dossier tab, or turn it off and assign specific dossier sections only. View-only applies only when this user can open applicant dossiers.",
    prefixGranularKeys: ["leads", "leads_import"],
    exclusiveFullKey: "applications",
    postExclusiveGranularKeys: [APPLICATIONS_VIEW_ONLY_PERMISSION_KEY],
    granularKeys: DOSSIER_TAB_PERMISSION_KEYS,
  },
  {
    id: "projects",
    title: "Projects",
    infoOnly: true,
    description:
      "Beneficiaries, Timeline approvals, and Project initiation use the same permission as full applicant access — enable **Applicants — full access (all dossier tabs + registry)** under Leads above.",
    granularKeys: [],
  },
  {
    id: "procurement",
    title: "Procurement",
    granularKeys: ["inventory_issue_po", "inventory_issue_material"],
  },
  {
    id: "inventory",
    title: "Inventory",
    description:
      "When any inventory permission below is enabled, Console Users will ask which **stores / yards** this account may use (or “all”). Goods receipts (Operations) use the same store list.",
    granularKeys: ["inventory_inward", "inventory_outward", "inventory_store_stocks"],
  },
  {
    id: "masters",
    title: "Masters",
    granularKeys: [
      "subcontractor_masters",
      "agents",
      "geo_directory",
      "geo_directory_admin",
      "milestone_templates",
      "construction_timeline",
      "standard_resources",
      "standard_item_catalogue",
      "geo_zones",
      "inventory_stores_yards",
    ],
  },
  {
    id: "operations",
    title: "Operations",
    description:
      "The cross-beneficiary Milestones hub also requires **full applicant access** (Leads) plus **Milestone master** under Masters.",
    granularKeys: ["bills_inward", "bills_outward", "inventory_goods_receipts"],
  },
  {
    id: "administration",
    title: "Administration",
    granularKeys: ["console_users", "audit_log"],
  },
];
